/**
 * The workspace's AI-credit balance, for any surface that shows it.
 *
 * ONE value shared by every consumer. Each hook instance used to hold its own
 * useState, so two chips mounted at once (the MMM hero and the export modal,
 * the chat header and the page hero) each fetched separately and then drifted:
 * one refreshed after a chat turn, the other kept showing the old number. Two
 * balances on one screen is the precise confusion the ledger exists to prevent.
 *
 * The store is a module-level snapshot plus a subscriber set: any refresh
 * updates every mounted chip, and a newly mounted one reads what is already
 * known instead of flashing empty.
 *
 * @module hooks/useCreditBalance
 */

import { useCallback, useEffect, useState } from "react";
import { fetchCreditBalance, fetchCreditRate, isAiCreditsEnabled } from "@/services/creditService";
import { useWorkspace } from "@/contexts/WorkspaceContext";

interface CreditSnapshot {
  workspaceId: string | null;
  balance: number | null;
  rate: number | null;
}

let snapshot: CreditSnapshot = { workspaceId: null, balance: null, rate: null };
const subscribers = new Set<(s: CreditSnapshot) => void>();

const publish = (next: CreditSnapshot): void => {
  snapshot = next;
  for (const notify of subscribers) notify(next);
};

/** Exported for tests: forget everything, as on a fresh page load. */
export const resetCreditSnapshot = (): void => {
  publish({ workspaceId: null, balance: null, rate: null });
};

export interface CreditBalanceState {
  /** null = credits disabled, no workspace yet, or the read failed. */
  balance: number | null;
  /**
   * Credits charged per successful AI call for this workspace. The master can
   * change it per workspace, so no surface may assume 1.
   */
  rate: number | null;
  /** Re-read after an action that spends credits — every chip updates. */
  refresh: () => Promise<void>;
}

export const useCreditBalance = (): CreditBalanceState => {
  const { workspace } = useWorkspace();
  const workspaceId = workspace?.id ?? null;
  // Start from what the store already knows for THIS workspace; a chip mounting
  // later should not blank out while it re-fetches.
  const [local, setLocal] = useState<CreditSnapshot>(() =>
    snapshot.workspaceId === workspaceId ? snapshot : { workspaceId, balance: null, rate: null },
  );

  useEffect(() => {
    const notify = (s: CreditSnapshot) => setLocal(s);
    subscribers.add(notify);
    return () => {
      subscribers.delete(notify);
    };
  }, []);

  const refresh = useCallback(async (): Promise<void> => {
    if (!isAiCreditsEnabled() || !workspaceId) {
      publish({ workspaceId, balance: null, rate: null });
      return;
    }
    const [nextBalance, nextRate] = await Promise.all([
      fetchCreditBalance(workspaceId),
      fetchCreditRate(workspaceId),
    ]);
    // A failed read leaves the last known number on screen rather than
    // flashing the chip away — a transient error is not "no credits".
    publish({
      workspaceId,
      balance: nextBalance ?? (snapshot.workspaceId === workspaceId ? snapshot.balance : null),
      rate: nextRate ?? (snapshot.workspaceId === workspaceId ? snapshot.rate : null),
    });
  }, [workspaceId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // A stale snapshot from another workspace must never be shown as this one's.
  const forThisWorkspace = local.workspaceId === workspaceId;
  return {
    balance: forThisWorkspace ? local.balance : null,
    rate: forThisWorkspace ? local.rate : null,
    refresh,
  };
};

export default useCreditBalance;
